import type { Mark } from '@atlaskit/editor-prosemirror/model';

import { COMMENT_BATCH_SIZE, CommentActionType, CommentType } from '@confluence/comments-data';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils';
import type {
	AnnotationStatus,
	CommentData,
	CommentsDataMap,
	RemovedThreadsCommentTypeMap,
	ReplyData,
} from '@confluence/comments-data';
import { fg } from '@confluence/feature-gating';
import type {
	CommentInlineLocation,
	CommentFooterLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries';

import { SortValues } from './useCommentsPanel';

const SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS = 8;
const NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW = 60;

const getTotalOffsetAboveCommentsPanelThreads = (generalCommentsHeaderHeight: number): number => {
	let objectSidebarPanelHeader = document.querySelector(
		"[data-testid='object-sidebar-panel-header']",
	);
	let additionalOffset = 0;
	if (!objectSidebarPanelHeader) {
		objectSidebarPanelHeader = document.querySelector(
			"[data-testid='scrolled-object-sidebar-panel-header']",
		);
		additionalOffset = SCROLLED_COMMENTS_HEADER_GAP_SIZE_IN_PIXELS;
	}

	const objectSidebarPanelHeaderHeight =
		objectSidebarPanelHeader?.getBoundingClientRect().height ?? 0;
	const objectHeaderHeight =
		document.querySelector("[data-testid='object-header-container']")?.getBoundingClientRect()
			.height ?? 0;
	const topNavContainerHeight =
		document.querySelector("[data-testid='grid-topNav']")?.getBoundingClientRect().height ?? 0;
	const bannerContainerHeight =
		document.querySelector("[data-testid='banner-container']")?.getBoundingClientRect().height ?? 0;
	const editorControlsOnboardingBannerHeight =
		document
			.querySelector("[data-testid='editor-controls-onboarding-banner']")
			?.getBoundingClientRect().height ?? 0;
	const announcementBannerHeight =
		document.querySelector("[id='AkBanner']")?.getBoundingClientRect().height ?? 0;

	return (
		objectSidebarPanelHeaderHeight +
		objectHeaderHeight +
		topNavContainerHeight +
		bannerContainerHeight +
		additionalOffset +
		generalCommentsHeaderHeight +
		editorControlsOnboardingBannerHeight +
		announcementBannerHeight
	);
};

export const shouldScrollCommentThreadToTop = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();
	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? document.querySelector("[data-testid='comment-section-separator']")?.getBoundingClientRect()
					.height ?? 0
			: 0;
	const totalPaddingAboveCommentThreadView = getTotalOffsetAboveCommentsPanelThreads(
		generalCommentsHeaderHeight,
	);

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;
	const viewportTop = totalPaddingAboveCommentThreadView;
	const viewportBottom = window.innerHeight - objectSidebarPanelFooterHeight;
	const commentTopInView = commentThreadRect.top >= viewportTop;
	const commentBottomInView = commentThreadRect.bottom <= viewportBottom;
	if (commentTopInView && commentBottomInView) {
		// Comment is 100% in view
		return false;
	} else if (!commentTopInView && commentBottomInView) {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	} else if (commentTopInView && !commentBottomInView) {
		// Comment is <100% in view and the hidden part is the bottom of the comment
		const hiddenBottomHeight = commentThreadRect.bottom - viewportBottom;
		if (hiddenBottomHeight <= NUM_HIDDEN_PIXELS_TO_BE_CONSIDERED_OUT_OF_VIEW) {
			// The hidden part is <= 60 pixels
			return false;
		} else {
			// The hidden part is > 60 pixels
			return true;
		}
	} else {
		// Comment is <100% in view and the hidden part is the top part of the comment
		return true;
	}
};

// This function is used when we want the entire comment thread to be in view, such as after the user clicks the reply button
export const isCommentThreadReplyOutOfView = (
	commentThreadElement: Element,
	commentType?: CommentType,
): boolean => {
	const commentThreadRect = commentThreadElement.getBoundingClientRect();

	const generalCommentsHeaderHeight =
		commentType === CommentType.GENERAL
			? document.querySelector("[data-testid='comment-section-separator']")?.getBoundingClientRect()
					.height ?? 0
			: 0;

	const objectSidebarPanelFooter = document.querySelector(
		"[data-testid='object-sidebar-panel-footer']",
	);
	const objectSidebarPanelFooterHeight =
		objectSidebarPanelFooter?.getBoundingClientRect().height ?? 0;

	return (
		commentThreadRect.bottom + generalCommentsHeaderHeight >
		window.innerHeight - objectSidebarPanelFooterHeight // Element is partially out of view below the comments panel viewport
	);
};

export enum LocationValue {
	PANEL = 'panel',
	FOOTER = 'footer',
	STANDALONE = 'standalone',
}

export const scrollCommentsPanel = ({
	containerId,
	commentMarkerRef,
	commentType,
	scrollToEnd,
	hasKeyPressed,
	annotationClicked,
	scrollOnLoad,
}: {
	containerId: string;
	commentMarkerRef: string;
	commentType?: CommentType;
	scrollToEnd?: boolean;
	hasKeyPressed?: boolean;
	annotationClicked?: boolean;
	scrollOnLoad?: boolean;
}) => {
	const container = document.querySelector(`[data-testid='${containerId}']`);
	if (container) {
		const element = document.querySelector(`[data-testid='${commentMarkerRef}']`);
		if (element) {
			const activeElement = document.activeElement;
			const isReplyButton = activeElement?.getAttribute('data-testid') === 'comment-reply-button';
			if (isReplyButton) {
				const replyContainer = document.querySelector(
					'[data-testid=comments-panel-new-reply-container]',
				);
				replyContainer?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
				return;
			}
			if (fg('comments_panel_scrolling_behavior_v1')) {
				const isActiveElementInView =
					activeElement &&
					(() => {
						const activeElementRect = activeElement.getBoundingClientRect();
						return (
							activeElementRect.top >= 0 &&
							activeElementRect.left >= 0 &&
							activeElementRect.bottom <= window.innerHeight &&
							activeElementRect.right <= window.innerWidth
						);
					})();

				if (!isActiveElementInView || hasKeyPressed) {
					if (fg('comments_panel_updated_scrolling_behavior')) {
						if (
							(scrollToEnd && isCommentThreadReplyOutOfView(element, commentType)) ||
							shouldScrollCommentThreadToTop(element, commentType)
						) {
							element.scrollIntoView({ behavior: 'smooth', block: scrollToEnd ? 'end' : 'start' });
						}
					} else {
						element.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				}
			} else {
				// If triggered by keyboard navigation or focus or if annotationClicked is true or triggered by a focused loading
				if (
					hasKeyPressed ||
					annotationClicked ||
					(scrollOnLoad && fg('confluence_frontend_comments_4742'))
				) {
					if (fg('comments_panel_updated_scrolling_behavior')) {
						if (
							(scrollToEnd && isCommentThreadReplyOutOfView(element, commentType)) ||
							shouldScrollCommentThreadToTop(element, commentType)
						) {
							element.scrollIntoView({ behavior: 'smooth', block: scrollToEnd ? 'end' : 'start' });
						}
					} else {
						element.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
					return;
				}
			}
		}
	}
};

export const getAnnotationsToLoad = (orderedActiveAnnotationIdList: AnnotationStatus[]) => {
	return orderedActiveAnnotationIdList
		.slice(0, Math.min(orderedActiveAnnotationIdList.length, COMMENT_BATCH_SIZE))
		.filter((item) => !item.isLoaded) // only get the ones that are not loaded yet
		.map((item) => item.threadKey);
};

const getDanglingComments = ({
	commentsDataMap,
	annotationsSet,
	openCommentThreads,
}: {
	commentsDataMap: CommentsDataMap;
	annotationsSet: Set<string> | undefined;
	openCommentThreads: CommentData[];
}) => {
	// Add dangling comments and handle real-time changes
	for (const threadKey in commentsDataMap.inline) {
		const commentThread = commentsDataMap.inline[threadKey];
		const isDanglingComment = (commentThread?.location as CommentInlineLocation)
			?.inlineResolveProperties?.isDangling;

		if (commentThread?.isOpen) {
			// if the comment is already dangling, we want to add it to the list
			if (isDanglingComment) {
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

				// comment is no longer dangling if it has a marker ref and is in the annotations set
				if (markerRef && annotationsSet && annotationsSet.has(markerRef)) {
					const updatedCommentThread = {
						...commentThread,
						location: {
							...commentThread.location,
							inlineResolveProperties: {
								...((commentThread.location as CommentInlineLocation)
									.inlineResolveProperties as ActiveCommentInlineResolveProperties),
								isDangling: false,
							},
						},
					};
					openCommentThreads.push(updatedCommentThread);
				} else {
					openCommentThreads.push(commentThread);
				}

				continue;
			}

			// check for real time dangling comments
			const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;

			// comment is dangling if it has a marker ref and is not in the annotations set
			// deleted and resolved comments are not in commentsDataMap so we know it's only dangling scenario
			// we do this here so the view will update immediately, but the actual mutation is handled in useDanglingOperations
			if (markerRef && annotationsSet && !annotationsSet.has(markerRef)) {
				const updatedCommentThread = {
					...commentThread,
					location: {
						...commentThread.location,
						inlineResolveProperties: {
							...((commentThread.location as CommentInlineLocation)
								.inlineResolveProperties as ActiveCommentInlineResolveProperties),
							isDangling: true,
						},
					},
				};
				openCommentThreads.push(updatedCommentThread);
			}
		}
	}
};

// organize a list of open comment threads that we have data for
export const getOpenCommentThreads = ({
	commentsDataMap,
	orderedActiveAnnotationIdList,
	removedThreadsMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: CommentsDataMap;
	orderedActiveAnnotationIdList: AnnotationStatus[];
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const openCommentThreads: CommentData[] = [];
	const annotationsSet = new Set(orderedActiveAnnotationIdList.map((item) => item.threadKey));

	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		const removedAnnotationIds = new Set(Object.values(removedThreadsMap.inline));
		const filteredActiveAnnotationIds = orderedActiveAnnotationIdList.filter(
			(item) => !removedAnnotationIds.has(item.threadKey),
		);
		const totalAnnotationsCount =
			filteredActiveAnnotationIds.length + Object.keys(removedThreadsMap).length;

		let activeIndex = 0; // Track position in filteredActiveAnnotationIds
		for (let i = 0; i < totalAnnotationsCount; i++) {
			let threadKey = '';

			if (removedThreadsMap.inline.hasOwnProperty(i)) {
				threadKey = removedThreadsMap.inline[i];
				const commentThread = commentsDataMap.inline[threadKey];
				if (commentThread) {
					openCommentThreads.push(commentThread);
				}
			} else if (activeIndex < filteredActiveAnnotationIds.length) {
				threadKey = filteredActiveAnnotationIds[activeIndex].threadKey;
				const commentThread = commentsDataMap.inline[threadKey];

				// dangling comments had a marker ref and will be inside commentsDataMap, so we want to ignore these first
				const isDanglingComment =
					(commentThread?.location as CommentInlineLocation)?.inlineResolveProperties?.isDangling &&
					fg('confluence_frontend_dangling_comments');

				// Don't add if it's dangling or might become dangling (has marker ref but not in annotations set)
				const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;
				const mightBecomeDangling = markerRef && annotationsSet && !annotationsSet.has(markerRef);

				if (commentThread?.isOpen && !isDanglingComment && !mightBecomeDangling) {
					openCommentThreads.push(commentThread);
				}
				activeIndex++;
			}
		}

		if (fg('confluence_frontend_dangling_comments')) {
			getDanglingComments({
				commentsDataMap,
				openCommentThreads,
				annotationsSet,
			});
		}
	}

	// general comments
	if (showGeneralComments || location === LocationValue.FOOTER) {
		// We can just push these in the order we have them because we have no concept of "active"
		const generalComments = Object.values(commentsDataMap.general).filter((generalComment) => {
			return (
				generalComment.isOpen ||
				generalComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT
			);
		});
		openCommentThreads.push(...generalComments);
	}

	return openCommentThreads;
};

// organize a list of unread comment threads that we have data for
export const getUnreadCommentThreads = ({
	numUnreadComments,
	commentsDataMap,
	orderedActiveAnnotationIdList,
	removedThreadsMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	numUnreadComments: number;
	commentsDataMap: CommentsDataMap;
	orderedActiveAnnotationIdList: AnnotationStatus[];
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const unreadCommentThreads: CommentData[] = [];

	if (
		(Object.keys(commentsDataMap.inline).length > 0 ||
			Object.keys(commentsDataMap.general).length > 0) &&
		(numUnreadComments > 0 || Object.keys(removedThreadsMap.inline).length > 0)
	) {
		// inline comments
		if (showInlineComments && location === LocationValue.PANEL) {
			const removedAnnotationIds = new Set(Object.values(removedThreadsMap.inline));
			const filteredActiveAnnotationIds = orderedActiveAnnotationIdList.filter(
				(item) => !removedAnnotationIds.has(item.threadKey),
			);
			const totalAnnotationsCount =
				filteredActiveAnnotationIds.length + Object.keys(removedThreadsMap.inline).length;

			let activeIndex = 0; // Track position in filteredActiveAnnotationIds

			for (let i = 0; i < totalAnnotationsCount; i++) {
				let threadKey = '';

				if (removedThreadsMap.inline.hasOwnProperty(i)) {
					threadKey = removedThreadsMap.inline[i];
					const commentThread = commentsDataMap.inline[threadKey];
					if (commentThread?.isUnread || commentThread?.replies?.some((reply) => reply.isUnread)) {
						unreadCommentThreads.push(commentThread);
					}
				} else if (activeIndex < filteredActiveAnnotationIds.length) {
					threadKey = filteredActiveAnnotationIds[activeIndex].threadKey;
					const commentThread = commentsDataMap.inline[threadKey];
					if (commentThread?.isUnread || commentThread?.replies?.some((reply) => reply.isUnread)) {
						unreadCommentThreads.push(commentThread);
					}
					activeIndex++;
				}
			}
		}

		// general comments
		if (showGeneralComments) {
			// We can just push these in the order we have them because we have no concept of "active"
			const generalComments = Object.values(commentsDataMap.general).filter((generalComment) => {
				return (
					(generalComment.isOpen ||
						generalComment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT) &&
					(generalComment.isUnread || generalComment.replies.some((reply) => reply.isUnread))
				);
			});
			unreadCommentThreads.push(...generalComments);
		}
	}

	return unreadCommentThreads;
};

export const getResolvedCommentThreads = ({
	commentsDataMap,
	showInlineComments,
	showGeneralComments,
	location = LocationValue.PANEL,
}: {
	commentsDataMap: CommentsDataMap;
	showInlineComments: boolean;
	showGeneralComments: boolean;
	location?: LocationValue;
}) => {
	const resolvedCommentThreads: CommentData[] = [];
	// inline comments
	if (showInlineComments && location === LocationValue.PANEL) {
		for (const key in commentsDataMap.inline) {
			const comment = commentsDataMap.inline[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	// general comments
	if (showGeneralComments) {
		for (const key in commentsDataMap.general) {
			const comment = commentsDataMap.general[key];
			if (comment.isOpen === false) {
				resolvedCommentThreads.push(comment);
			}
		}
	}

	return resolvedCommentThreads;
};

const getCommentResolvedOrCreatedTime = (comment: CommentData): number => {
	const commentLocation = comment.location;
	const resolvedTime =
		(commentLocation as CommentInlineLocation)?.inlineResolveProperties?.resolvedTime ||
		(commentLocation as CommentFooterLocation)?.commentResolveProperties?.resolvedTime; // from the BE, this is for both reopened and resolved time, in milliseconds
	return resolvedTime || getValidDate(comment.createdAtNonLocalized).getTime(); // getTime() returns milliseconds
};

export const getSortedCommentThreads = (
	currentSort: SortValues,
	commentThreads: CommentData[],
): CommentData[] => {
	if (currentSort === SortValues.MOST_RECENT) {
		return [...commentThreads].sort(
			(a, b) => getCommentResolvedOrCreatedTime(b) - getCommentResolvedOrCreatedTime(a),
		);
	}

	return commentThreads;
};

export const getAnnotationThreadKeys = (annotations: AnnotationStatus[]) =>
	annotations.map((annotation) => annotation.threadKey);

export const getMarkIds = (marks: Mark[] = []) => marks.map((mark) => mark.attrs.id);

export const getCommentsSortedOldestToNewest = (
	commentA: CommentData | ReplyData,
	commentB: CommentData | ReplyData,
) =>
	getValidDate(commentA?.createdAtNonLocalized).getTime() -
	getValidDate(commentB?.createdAtNonLocalized).getTime();
